/**
 * Formatação de descrições de parâmetros HRN
 * Funções para formatar PO, FE, GS e NP em diferentes formatos
 * Utiliza as constantes definidas em hrn-tables.ts para manter consistência com a UI
 */

import {
  TABELA_PO,
  TABELA_FE,
  TABELA_GS,
  TABELA_NP,
  HRNTableValue
} from '../../constants/hrn-tables';

/**
 * Função auxiliar para encontrar a descrição mais próxima
 */
function findDescricao(tabela: HRNTableValue[], valor: number): string {
  // Tenta encontrar valor exato
  const itemExato = tabela.find(item => Math.abs(item.valor - valor) < 0.001);
  if (itemExato) return itemExato.descricao;

  // Se não encontrar, tenta encontrar o valor mais próximo
  // Ordena por diferença absoluta
  const sorted = [...tabela].sort((a, b) => Math.abs(a.valor - valor) - Math.abs(b.valor - valor));
  return sorted[0]?.descricao || '';
}

/**
 * Formata descrição completa de Probabilidade de Ocorrência (PO)
 * @param valor Valor numérico de PO
 * @returns Descrição formatada (ex: "Improvável (PO)")
 */
export function formatarDescricaoPO(valor: number): string {
  const descricao = findDescricao(TABELA_PO, valor);
  return `${descricao} (PO)`;
}

/**
 * Formata descrição de PO sem sufixo (PO) - para uso em tabelas técnicas
 * @param valor Valor numérico de PO
 * @returns Descrição formatada (ex: "Possível")
 */
export function formatarDescricaoPOTabela(valor: number): string {
  return findDescricao(TABELA_PO, valor);
}

/**
 * Formata descrição completa de Frequência de Exposição (FE)
 * @param valor Valor numérico de FE
 * @returns Descrição formatada (ex: "Em termos de hora (FE)")
 */
export function formatarDescricaoFE(valor: number): string {
  const descricao = findDescricao(TABELA_FE, valor);
  return `${descricao} (FE)`;
}

/**
 * Formata descrição de FE sem sufixo (FE) - para uso em tabelas técnicas
 * @param valor Valor numérico de FE
 * @returns Descrição formatada (ex: "Em termos de hora")
 */
export function formatarDescricaoFETabela(valor: number): string {
  return findDescricao(TABELA_FE, valor);
}

/**
 * Formata descrição completa de Grau de Severidade (GS)
 * @param valor Valor numérico de GS
 * @returns Descrição formatada (ex: "Quebra - ossos grandes – mão / braços / pernas (GS)")
 */
export function formatarDescricaoGS(valor: number): string {
  const descricao = findDescricao(TABELA_GS, valor);
  return `${descricao} (GS)`;
}

/**
 * Formata descrição simplificada de GS para tabelas técnicas
 * Usamos a descrição padrão pois hrn-tables.ts já possui descrições concisas
 * @param valor Valor numérico de GS
 * @returns Descrição formatada
 */
export function formatarDescricaoGSSimplificada(valor: number): string {
  return findDescricao(TABELA_GS, valor);
}

/**
 * Formata descrição completa de Nº de Pessoas Expostas ao Risco (NP)
 * @param valor Valor numérico de NP
 * @returns Descrição formatada (ex: "1-2 (NP)")
 */
export function formatarDescricaoNP(valor: number): string {
  const descricao = findDescricao(TABELA_NP, valor);
  return `${descricao} (NP)`;
}

/**
 * Formata descrição de NP sem sufixo (NP) - para uso em tabelas técnicas
 * @param valor Valor numérico de NP
 * @returns Descrição formatada (ex: "1–2")
 */
export function formatarDescricaoNPTabela(valor: number): string {
  return findDescricao(TABELA_NP, valor);
}
